import { type AuthenticateUserMutation } from '#app/__generated__/relay/AuthenticateUserMutation.graphql.js';
import { type DeauthenticateUserMutation } from '#app/__generated__/relay/DeauthenticateUserMutation.graphql.js';
import { Redirect } from '#app/components/Redirect/index.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { usePageContext } from '#app/hooks/usePageContext.js';
import { AuthenticateUser } from '#app/mutations/src/registration/AuthenticateUserMutation.js';
import { DeauthenticateUser } from '#app/mutations/src/registration/DeauthenticateUserMutation.js';
import { AuthContext } from './AuthContext.js';
import {
  // disable useContraMutation eslint error
  useMutation,
  type UseMutationConfig,
} from '@contra/relay';
import { getRoute } from '@contra/routes';
import { useCallback, useMemo } from 'react';

type AuthProviderProps = {
  readonly children: React.ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { reset } = useAnalytics();

  const toast = useToast();
  const [login, isLoggingIn] =
    useMutation<AuthenticateUserMutation>(AuthenticateUser);

  const [logoutMutation, isLoggingOut] =
    useMutation<DeauthenticateUserMutation>(DeauthenticateUser);

  const logout = useCallback(
    async (config: UseMutationConfig<DeauthenticateUserMutation>) => {
      logoutMutation({
        ...config,
        onCompleted: (response, errors) => {
          const {
            deauthenticate: { errors: mutationErrors },
          } = response;

          if (mutationErrors?.[0]) {
            toast.error(mutationErrors[0].message);
          } else {
            reset();
            if (config.onCompleted) config.onCompleted(response, errors);
          }
        },
      });
    },
    [logoutMutation, reset, toast],
  );

  const context = useMemo(() => {
    return { isLoggingIn, isLoggingOut, login, logout };
  }, [isLoggingIn, isLoggingOut, login, logout]);

  const currentUser = useCurrentUser();

  const {
    exports: { authenticationRequired },
  } = usePageContext();

  const location = useLocation();

  if (authenticationRequired && !currentUser) {
    return (
      <Redirect
        method="replace"
        to={getRoute('/log-in', {
          search: {
            redirectTo: location.pathname,
          },
        })}
      />
    );
  }

  return (
    <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
  );
};
